import { api } from './client';
import type { LeaveBalance, LeavePolicy, RequestDayStatus, AllocationSlice } from './leaves';

export type RequestStatus = 'Pending' | 'AwaitingHR' | 'Approved' | 'Rejected' | 'Cancelled';

export interface AttendanceTypeOption {
  id: number;
  name: string;
  label: string;
  color: string;
  sortOrder: number;
  isSystem: boolean;
  /** 'leave' = shown on the Leave Types catalog; 'presence' = attendance marker. */
  category?: 'leave' | 'presence';
  /** Leave policy rules (quota, carry-forward, backdate, etc.), null for presence types. */
  policy?: LeavePolicy | null;
  /** Human description injected client-side (see useAttendanceTypes). */
  description?: string | null;
}

export interface AdminAttendanceTypeOption extends AttendanceTypeOption {
  id: number;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
}

export interface AttendanceRequest {
  id: number;
  userId: number;
  attendanceTypeId: number;
  typeName: string;
  typeLabel: string;
  typeColor: string;
  dates: string[];
  customReason: string | null;
  attachmentPath: string | null;
  status: RequestStatus;
  lwpExcess: number;
  /** Employee accepted that uncovered days are recorded as Leave Without Pay. */
  allowLwp?: boolean;
  /** Optional-holiday dates were charged to the Optional Holiday quota. */
  useOptionalHolidays?: boolean;
  reviewedBy: number | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  /**
   * Set only when the leave type was overwritten after the request was raised
   * — the type it was raised as. The lifecycle `status` is unchanged by an
   * overwrite, so the two are shown side by side.
   */
  originalTypeLabel?: string | null;
  typeChangedAt?: string | null;
  typeChangeReason?: string | null;
  typeChangedByName?: string | null;
  /** Per-day status change this request causes (e.g. Absent → Sick Leave). */
  dayStatuses?: RequestDayStatus[];
  /** Per-type split returned when the request is created. */
  allocations?: AllocationSlice[];
}

export interface AdminAttendanceRequest extends AttendanceRequest {
  /** Whether the signed-in reviewer may approve or reject this request. */
  canReview?: boolean;
  /** Why not, when they may not — the same sentence the API would 403 with. */
  reviewBlockedReason?: string | null;
  userName: string;
  employeeId: string;
  departmentName: string | null;
  reviewedByName: string | null;
  reportingManagerId: number | null;
  reportingManagerName: string | null;
  /** True for two-step types: reporting manager first, then HR. */
  requiresHrApproval?: boolean;
}

export interface Holiday {
  date: string;
  name: string;
  /** For one person, the type at their office. */
  isMandatory: boolean;
  /** Only with `all`: per-office types (empty = same for all offices). */
  rules?: Array<{ locationId: number; locationName: string; isMandatory: boolean }>;
}

export function createAttendanceRequest(data: {
  attendanceTypeId: number;
  dates: string[];
  customReason: string;
  attachments?: File[];
  /** Employee accepted that uncovered days become Leave Without Pay. */
  acceptLwp?: boolean;
  /** Charge optional-holiday dates to the Optional Holiday quota (default true). */
  useOptionalHolidays?: boolean;
}): Promise<AttendanceRequest> {
  const form = new FormData();
  form.append('attendanceTypeId', String(data.attendanceTypeId));
  form.append('dates', JSON.stringify(data.dates));
  form.append('customReason', data.customReason);
  if (data.acceptLwp) form.append('acceptLwp', 'true');
  if (data.useOptionalHolidays === false) form.append('useOptionalHolidays', 'false');
  for (const file of data.attachments ?? []) form.append('attachments', file);
  return api.postForm<AttendanceRequest>('/api/attendance/requests', form);
}

export function getMyRequests(params?: {
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<{ requests: AttendanceRequest[] }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.startDate) qs.set('startDate', params.startDate);
  if (params?.endDate) qs.set('endDate', params.endDate);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return api.get(`/api/attendance/requests/me${q ? '?' + q : ''}`);
}

/** Cancel own pending request. Backend sets status to 'Cancelled' (never deletes). */
export function cancelMyRequest(id: number): Promise<AttendanceRequest> {
  return api.patch(`/api/attendance/requests/${id}/cancel`);
}

/**
 * Holidays by office. With no options, the signed-in employee's own;
 * `userId` gives another employee's (approvers / HR), `all` every holiday with its per-office types.
 */
export function getHolidays(
  year?: number,
  opts?: { userId?: number; all?: boolean },
): Promise<{ holidays: Holiday[] }> {
  const qs = new URLSearchParams();
  if (year) qs.set('year', String(year));
  if (opts?.userId) qs.set('userId', String(opts.userId));
  if (opts?.all) qs.set('all', 'true');
  const q = qs.toString();
  return api.get(`/api/attendance/holidays${q ? '?' + q : ''}`);
}

export function getAttendanceTypes(): Promise<{ types: AttendanceTypeOption[] }> {
  return api.get('/api/attendance/types');
}

// Admin attendance types
export function getAdminAttendanceTypes(): Promise<{ types: AdminAttendanceTypeOption[] }> {
  return api.get('/api/admin/attendance-types');
}

export function createAdminAttendanceType(data: { name: string; label: string; color: string; sortOrder?: number }): Promise<AdminAttendanceTypeOption> {
  return api.post('/api/admin/attendance-types', data);
}

export function updateAdminAttendanceType(id: string, data: { label?: string; color?: string; isActive?: boolean; sortOrder?: number }): Promise<AdminAttendanceTypeOption> {
  return api.patch(`/api/admin/attendance-types/${id}`, data);
}

export function deleteAdminAttendanceType(id: string): Promise<void> {
  return api.delete(`/api/admin/attendance-types/${id}`);
}

/** One day of the range the employee has already applied for and is still awaiting a decision on. */
export interface PendingDateConflict {
  date: string;
  requestId: number;
  status: string;
  typeLabel: string;
}

export function getMyPendingDateConflicts(dates: string[]): Promise<{ conflicts: PendingDateConflict[] }> {
  return api.get(`/api/attendance/requests/conflicts?dates=${dates.join(',')}`);
}

export function getMyAttendanceRange(startDate: string, endDate: string): Promise<{ records: Array<{ id: number; date: string; typeName: string; typeLabel: string; typeColor: string }> }> {
  return api.get(`/api/attendance/me?startDate=${startDate}&endDate=${endDate}&limit=200`);
}

export function getAdminAttendanceRange(userId: number, startDate: string, endDate: string): Promise<{ records: Array<{ id: number; date: string; typeName: string; typeLabel: string; typeColor: string }> }> {
  return api.get(`/api/admin/attendance/${userId}?startDate=${startDate}&endDate=${endDate}&limit=200`);
}

// Admin
export function getAdminRequests(params?: {
  status?: string;
  userId?: string;
  departmentId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<{ requests: AdminAttendanceRequest[] }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.userId) qs.set('userId', params.userId);
  if (params?.departmentId) qs.set('departmentId', params.departmentId);
  if (params?.startDate) qs.set('startDate', params.startDate);
  if (params?.endDate) qs.set('endDate', params.endDate);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return api.get(`/api/admin/attendance/requests${q ? '?' + q : ''}`);
}

export function approveRequest(id: string): Promise<AdminAttendanceRequest & { datesCreated?: string[]; lwpDates?: string[]; datesSkipped?: string[] }> {
  return api.patch(`/api/admin/attendance/requests/${id}/approve`);
}

export function rejectRequest(id: string, rejectionReason?: string): Promise<AdminAttendanceRequest> {
  return api.patch(`/api/admin/attendance/requests/${id}/reject`, { rejectionReason });
}

export function bulkApproveRequests(ids: number[]): Promise<{
  approved: number; movedToHr?: number; datesCreated: number; datesSkipped: number;
  /** Requests that would now need unpaid days the employee never accepted. */
  blocked?: Array<{ id: number; reason: string }>;
}> {
  return api.patch('/api/admin/attendance/requests/bulk-approve', { ids });
}

export function bulkRejectRequests(ids: number[], rejectionReason?: string): Promise<{
  rejected: number;
  /** Requests still awaiting their reporting manager's decision. */
  blocked?: Array<{ id: number; reason: string }>;
}> {
  return api.patch('/api/admin/attendance/requests/bulk-reject', { ids, rejectionReason });
}

export interface PreviewDate {
  date: string;
  /** null = no existing record (new) */
  currentTypeId: number | null;
  currentTypeName: string | null;
  currentTypeLabel: string | null;
  currentTypeColor: string | null;
  isOverwrite: boolean;
  /** Type the day will actually be recorded as (may be a fallback type or LWP). */
  allocatedTypeId: number | null;
  allocatedTypeName: string | null;
  allocatedTypeLabel: string | null;
  allocatedTypeColor: string | null;
  isLocked: boolean;
}

export interface RequestPreview {
  requestId: number;
  attendanceTypeId: number;
  typeName: string;
  typeLabel: string;
  typeColor: string;
  dates: string[];
  willCreate: PreviewDate[];
  willSkip: Array<{ date: string; reason: string }>;
  /** The employee's leave balances, so an approver can weigh the request. */
  balances?: LeaveBalance[];
  lwpDaysUsed?: number;
}

export function previewRequest(id: string | number): Promise<RequestPreview> {
  return api.get<RequestPreview>(`/api/admin/attendance/requests/${id}/preview`);
}

export interface PendingApprovalCounts {
  direct: number;
  global: number;
  total: number;
  items: { id: number; userName: string; typeLabel: string; typeColor: string; dates: string[]; isDirect: boolean }[];
}

export function getPendingApprovalCounts(): Promise<PendingApprovalCounts> {
  return api.get('/api/admin/attendance/requests/pending-counts');
}
